

import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import LessonFormModal from './AddLessonModal';
import Swal from 'sweetalert2';
import { toast } from "react-toastify";
import { 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaSearch, 
  FaFilter, 
  FaSpinner,
  FaBook,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';

// --- Constants ---
const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/v1/admin`;
const ITEMS_PER_PAGE = 10; 

// --- Helper Components ---
const ActionButton = ({ onClick, children, className }) => (
    <button onClick={onClick} className={`p-1.5 rounded-md transition-colors ${className}`}>
        {children}
    </button>
);

const EditIcon = () => <FaEdit className="h-5 w-5" />;
const DeleteIcon = () => <FaTrash className="h-5 w-5" />;

// --- Main Component ---
const ManageLessonTable = () => {
    const [lessons, setLessons] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    
    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [editingLessonId, setEditingLessonId] = useState(null);

    // Search and Filter State
    const [searchQuery, setSearchQuery] = useState('');
    const [lessonType, setLessonType] = useState(''); // 'normal', 'bonus', or '' for all
    const [activeFilters, setActiveFilters] = useState({ query: '', type: '' });

    const fetchAllLessons = useCallback(async (page, filters) => {
        setIsLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({
                page: page,
                limit: ITEMS_PER_PAGE,
            });
            if (filters.type) {
                params.append('lessonType', filters.type);
            }
            if (filters.query) {
                params.append('searchByTitle', filters.query); 
            }

            const response = await axios.get(`${API_BASE_URL}/all-lessons?${params.toString()}`);
            
            if (response?.data?.success) {
                setLessons(response.data.data || []);
                setCurrentPage(response.data.page || 1);
                setTotalPages(response.data.totalPages || 0);
                setTotalItems(response.data.totalItems || 0);
                if (response.data.data.length === 0 && response.data.totalItems > 0 && page > 1) {
                    setCurrentPage(prev => Math.max(1, prev - 1));
                }
            } else { throw new Error(response?.data?.error || 'Invalid response'); }
        } catch (err) {
            const msg = err.response?.data?.error || err.message || 'Failed to fetch lessons.';
            setError(msg);
            setLessons([]); setTotalPages(0); setTotalItems(0);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAllLessons(currentPage, activeFilters);
    }, [currentPage, activeFilters, fetchAllLessons]);


    useEffect(() => {
    const delayDebounce = setTimeout(() => {
        setCurrentPage(1);
        setActiveFilters({ query: searchQuery.trim(), type: lessonType });
    }, 500); // wait 500ms after user stops typing

    return () => clearTimeout(delayDebounce);
}, [searchQuery, lessonType]);

    const handleSearch = () => {
        setCurrentPage(1); 
        setActiveFilters({ query: searchQuery, type: lessonType });
    };

    const handleReset = () => {
        setCurrentPage(1);
        setSearchQuery('');
        setLessonType('');
        setActiveFilters({ query: '', type: '' });
    };
    
    // FIX: New handler to trigger search on dropdown change
    const handleLessonTypeChange = (newType) => {
        setLessonType(newType); // Update the visual state of the dropdown
        setCurrentPage(1); // Reset to page 1 for new filter
        setActiveFilters({ query: searchQuery, type: newType }); // Trigger the API call
    };

    const handleSuccess = (message, mode) => {
        setSuccessMessage(message);
        setTimeout(() => setSuccessMessage(''), 3000); 
        if (mode === 'add' && (currentPage !== 1 || activeFilters.query || activeFilters.type)) {
             handleReset(); 
        } else {
            fetchAllLessons(currentPage, activeFilters);
        }
    };
    
const handleDelete = async (lessonId, lessonTitle) => {
    const result = await Swal.fire({
        title: `Are you sure you want to delete this lesson?`,
        text: "This will permanently delete the lesson. This action cannot be undone.",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete lesson!',
        cancelButtonText: 'Cancel',
        customClass: {
            confirmButton: 'swal2-confirm-button',
            cancelButton: 'swal2-cancel-button'
        }
    });

    if (result.isConfirmed) {
        try {
            const token = localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const response = await axios.delete(`${API_BASE_URL}/delete-lesson/${lessonId}`, { headers });

            toast.success(response.data.message || 'Lesson deleted successfully.');
            fetchAllLessons(currentPage, activeFilters);
        } catch (err) {
            console.error('Error deleting lesson:', err);
            toast.error(err.response?.data?.error || 'Failed to delete lesson.');
        } 
        
    }
};

    
    const handleOpenModalForAdd = () => {
        setModalMode('add');
        setEditingLessonId(null);
        setIsModalOpen(true);
    };

    const handleOpenModalForEdit = (lessonId) => {
        setModalMode('edit');
        setEditingLessonId(lessonId);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => setIsModalOpen(false);
    
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage && !isLoading) {
            setCurrentPage(newPage);
        }
    };
    
    const PaginationControls = () => {
        if (!totalPages || totalPages <= 0) return null;
        const getPageNumbers = () => {
            const pageNumbers = [];
            const maxPagesToShow = 5;
            const halfMaxPages = Math.floor(maxPagesToShow / 2);
            if (totalPages <= maxPagesToShow) {
                for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
            } else {
                let startPage = Math.max(currentPage - halfMaxPages, 1);
                let endPage = Math.min(startPage + maxPagesToShow - 1, totalPages);
                if (endPage - startPage < maxPagesToShow - 1) {
                    startPage = Math.max(endPage - maxPagesToShow + 1, 1);
                }
                if (startPage > 1) {
                    pageNumbers.push(1, '...');
                }
                for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);
                if (endPage < totalPages) {
                    pageNumbers.push('...', totalPages);
                }
            }
            return pageNumbers;
        };
        // FIX: Simplified pagination text to match image
        const pageDisplayInfo = `Page ${currentPage} of ${totalPages}`;
        return (
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                        Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, totalItems)} of {totalItems} results
                    </div>
                    
                    <nav className="flex items-center gap-1" aria-label="Pagination">
                        <button 
                            onClick={() => handlePageChange(currentPage - 1)} 
                            disabled={currentPage === 1 || isLoading} 
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                        >
                            <FaChevronLeft className="w-4 h-4" />
                            Previous
                        </button>
                        
                        <div className="flex items-center gap-1">
                        {getPageNumbers().map((page, index) => (
                            typeof page === 'number' ? (
                                    <button 
                                        key={index} 
                                        onClick={() => handlePageChange(page)} 
                                        disabled={isLoading} 
                                        className={`inline-flex items-center justify-center w-10 h-10 text-sm font-medium rounded-lg transition-colors duration-200 ${
                                            currentPage === page 
                                                ? 'bg-blue-600 text-white shadow-lg' 
                                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                                        }`}
                                    >
                                        {page}
                                    </button>
                                ) : (
                                    <span key={index} className="inline-flex items-center justify-center w-10 h-10 text-sm font-medium text-gray-500 dark:text-gray-400">
                                        {page}
                                    </span>
                                )
                            ))}
                        </div>
                        
                        <button 
                            onClick={() => handlePageChange(currentPage + 1)} 
                            disabled={currentPage === totalPages || isLoading} 
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                        >
                            Next
                            <FaChevronRight className="w-4 h-4" />
                        </button>
                    </nav>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-full w-full max-w-full overflow-x-auto">
            {/* Header Section */}
            <div className="mb-8">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                            Lesson Management
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400">
                            Manage your lesson content and configurations
                        </p>
                    </div>
                    <button 
                        onClick={handleOpenModalForAdd} 
                        className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                    >
                        <FaPlus className="w-5 h-5" />
                        Add New Lesson
                    </button>
                </div>
                </div>
                

            {/* Search and Filter Section */}
            <div className="mb-6 p-4 sm:p-6 bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-x-auto max-w-full">
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                        <FaSearch className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Search & Filter</h3>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
                    <div className="relative">
                        <label htmlFor="search-title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Search by Title</label>
                        <div className="relative">
                            <input 
                                type="text" 
                                id="search-title" 
                                value={searchQuery} 
                                onChange={(e) => setSearchQuery(e.target.value)} 
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()} 
                                className="block w-full px-4 py-3 pl-10 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-all duration-200 text-gray-900 dark:text-gray-100" 
                                placeholder="Enter lesson title..."
                            />
                            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                          {searchQuery && (
                            <button
                            type="button"
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                            onClick={() => {
                                setSearchQuery('');
                                setActiveFilters((prevFilters) => ({ ...prevFilters, query: '' }));
                                        setCurrentPage(1);
                            }}
                            >
                            ✕
                            </button>
                        )}
                        </div>
                    </div>
                        
                        <div>
                        <label htmlFor="lesson-type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Lesson Type</label>
                        <div className="relative">
                            <select 
                                id="lesson-type" 
                                value={lessonType} 
                                onChange={(e) => handleLessonTypeChange(e.target.value)} 
                                className="block w-full px-4 py-3 pl-10 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-all duration-200 text-gray-900 dark:text-gray-100"
                            >
                                <option value="">All Types</option>
                                <option value="normal">Normal Lesson</option>
                                <option value="bonus">Bonus Lesson</option>
                            </select>
                            <FaFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        </div>
                        </div>
                    
                    <div className="flex space-x-3">
                        <button 
                            onClick={handleSearch} 
                            className="flex-1 inline-flex items-center justify-center gap-2 py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        >
                            <FaSearch className="w-4 h-4" />
                            Search
                        </button>
                        <button 
                            onClick={handleReset} 
                            className="flex-1 inline-flex items-center justify-center gap-2 py-3 px-4 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-xl border border-gray-300 dark:border-gray-600 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                        >
                            Reset
                        </button>
                        </div>
                    </div>
                </div>

                <LessonFormModal isOpen={isModalOpen} onClose={handleCloseModal} onSuccess={handleSuccess} mode={modalMode} lessonId={editingLessonId} />

            {/* Lessons Table */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
                {isLoading && lessons.length === 0 ? (
                    <div className="p-12 text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full mb-4">
                            <FaSpinner className="animate-spin h-8 w-8 text-blue-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Loading Lessons</h3>
                        <p className="text-gray-600 dark:text-gray-400">Please wait while we fetch your lessons...</p>
                    </div>
                ) : !isLoading && lessons.length === 0 ? (
                    <div className="p-12 text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
                            <FaBook className="w-8 h-8 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Lessons Found</h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            {(activeFilters.query || activeFilters.type) 
                                ? 'Try adjusting your search filters or add a new lesson.' 
                                : 'Get started by adding your first lesson.'
                            }
                        </p>
                        <button
                            onClick={handleOpenModalForAdd}
                            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl transition-colors duration-200"
                        >
                            <FaPlus className="w-5 h-5" />
                            Add First Lesson
                        </button>
                    </div>
                ) : (
                    <>
                        {/* Table Header */}
                        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    Lessons ({totalItems})
                                </h3>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-500 dark:text-gray-400">
                                        Page {currentPage} of {totalPages}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Table Content */}
                        <div className="overflow-x-auto overflow-y-auto max-h-96 w-full -mx-4 sm:mx-0">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" >
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr className='whitespace-nowrap'>
                                        <th className="sticky left-0 bg-gray-50 dark:bg-gray-700 px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider z-20 shadow-sm">#</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">Lesson #</th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">Title</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">Type</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">Level</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">Investment</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">Effort</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">Style</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">Tech</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">Group</th>
                                        <th className="px-4 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">Priority</th>
                                        <th className="sticky right-0 bg-gray-50 dark:bg-gray-700 px-6 py-4 text-center text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider z-20 shadow-sm">Actions</th>
                                </tr>
                            </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {lessons.map((lesson, index) => (
                                        <tr key={lesson._id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150">
                                            <td className="sticky left-0 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white z-10">
                                                {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300 font-mono">{lesson.lessonNumber}</td>
                                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-white font-medium max-w-sm">
                                                <div className="truncate" title={lesson.lessonTitle}>
                                                    {lesson.lessonTitle}
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                    lesson.lessonType === 'bonus' 
                                                        ? 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-200' 
                                                        : 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200'
                                                }`}>
                                                    {lesson.lessonType || 'normal'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{lesson.level}</td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">${lesson.minInvestment}</td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{lesson.effortLevel}</td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{lesson.workStyle}</td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{lesson.techComfort}</td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{lesson.group}</td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{lesson.priority}</td>
                                            <td className="sticky right-0 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 px-6 py-4 whitespace-nowrap text-sm font-medium z-10">
                                                <div className="flex items-center justify-center space-x-2">
                                                    <ActionButton 
                                                        onClick={() => handleOpenModalForEdit(lesson._id)} 
                                                        className="text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/20"
                                                    >
                                                        <EditIcon />
                                                    </ActionButton>
                                                    <ActionButton 
                                                        onClick={() => handleDelete(lesson._id, lesson.lessonTitle)} 
                                                        className="text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/20"
                                                    >
                                                        <DeleteIcon />
                                                    </ActionButton>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                    </>
                )}
                
                    <PaginationControls/>
            </div>
        </div>
    );
};

export default ManageLessonTable;